import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import type { SelectionExtension } from '@atlaskit/editor-plugin-selection-extension';
import { WorkItemIcon } from '@atlaskit/editor-toolbar';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useIssueCreateSidePanelContext } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';
import { ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanel.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';
import type { RightSidebarContextType } from '@confluence/page-layout-context';
import type { PageMode } from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider.ts';

import { messages } from '../messages.ts';
import { createSingleJiraWorkItemHandle } from '../jiraWorkCreateHandlers.ts';

export type UseJiraWorkCreateSelectionExtensionProps = {
	isSidePanelOpenRef: React.MutableRefObject<boolean>;
	rightSidebarContextRef: React.MutableRefObject<RightSidebarContextType>;
	pageModeRef: React.MutableRefObject<PageMode>;
	hasEditPermissionRef: React.MutableRefObject<boolean>;
};

const WorkItemCreationType = {
	SINGLE_WORK_ITEM: 'SINGLE_ISSUE',
	MULTIPLE_WORK_ITEM: 'MULTIPLE_ISSUE',
	AI_WORK_ITEM: 'AI_ISSUE',
} as const;

type WorkItemCreationType = (typeof WorkItemCreationType)[keyof typeof WorkItemCreationType];

/**
 * @deprecated Use useJiraWorkCreateSelectionExtensionV2 instead.
 * TODO: Remove this deprecated hook when cleaning up confluence-bulk-work-create-editor FG
 */
export const useJiraWorkCreateSelectionExtension = ({
	rightSidebarContextRef,
	pageModeRef,
	hasEditPermissionRef,
}: UseJiraWorkCreateSelectionExtensionProps): SelectionExtension => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useIssueCreateSidePanelContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireCreateWorkItemButtonClickedAnalytics = useCallback(
		(workItemCreationType: WorkItemCreationType, isCreatedFromTable: boolean) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType: workItemCreationType,
						// Set to false because a dropdown or flyout is not implemented at this entry point.
						// TODO: Update this value if a dropdown or flyout is added in the future.
						isDropdownItem: false,
						isCreatedFromTable,
						pageMode: pageModeRef.current,
						hasEditPermission: hasEditPermissionRef.current,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, pageModeRef, hasEditPermissionRef],
	);

	const handleSingleCreateClick = useCallback(async () => {
		actions.setHasEditPermission(hasEditPermissionRef.current);
		const activeSelection = window.getSelection();
		const container = document.querySelector<HTMLElement>('div#ak-editor-textarea');

		fireCreateWorkItemButtonClickedAnalytics(
			WorkItemCreationType.SINGLE_WORK_ITEM,
			// Set to false because bulk creation from the table is not yet available at this entry point.
			// TODO: Update this value if bulk creation functionality is added in the future.
			false,
		);

		createSingleJiraWorkItemHandle({
			activeSelection,
			container,
			contentId,
			contentType,
			rightSidebarContext: rightSidebarContextRef?.current,
			actions,
			pageMode: pageModeRef.current,
			editorAPI: undefined,
		});
	}, [
		fireCreateWorkItemButtonClickedAnalytics,
		contentId,
		contentType,
		rightSidebarContextRef,
		actions,
		pageModeRef,
		hasEditPermissionRef,
	]);

	return {
		name: formatMessage(messages.singleCreateWorkItemNameUpdated),
		icon: WorkItemIcon,
		onClick: () => {
			void handleSingleCreateClick();
		},
		isDisabled: () => {
			const currentSidebar = rightSidebarContextRef?.current?.getCurrent();
			return currentSidebar?.panelId === ISSUE_CREATE_SIDE_PANEL_ID;
		},
	};
};
